import { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, MapPin, Truck, Calendar, User, Car, CheckCircle2, Route as RouteIcon, PackageCheck, ChevronRight } from "lucide-react";
import PdaLayout from "./PdaLayout";
import usePdaScenarioHint from "@/hooks/usePdaScenarioHint";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * List of orders that have packed bags ready for delivery.
 *
 * Primary grouping is by DELIVERY RUN — the operator sees blocks for each
 * Run (driver + vehicle badge) and taps in to work through the stops.
 * Orders not yet assigned to any Run land in an "Unassigned" bucket at
 * the bottom.
 *
 * Optional secondary grouping by date is still available as a backup for
 * quick eyeballing during real-time operations.
 *
 * A second tab ("Completed") lets drivers scroll back through orders they
 * already delivered — same By-run / By-date grouping, sorted newest-first.
 */
export default function PdaDispatchList({ onLogout }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [orders, setOrders] = useState([]);
  // Group-by-date was surfaced early on as a backup filter but drivers
  // universally work by run. Hiding the toggle keeps the header cleaner
  // on small PDA screens. Locked to "run" to preserve grouping logic
  // downstream unchanged.
  const groupBy = "run";
  const [view, setView] = useState("ready"); // "ready" | "completed"
  // Track which run buckets are expanded. Persist across polls so a driver
  // who tapped a run open won't get collapsed when the list refreshes.
  // Persisted to sessionStorage so a page refresh also keeps it open until
  // the driver logs out.
  const [expanded, setExpanded] = useState(() => {
    try {
      const raw = sessionStorage.getItem("pda-dispatch-expanded");
      return raw ? new Set(JSON.parse(raw)) : new Set();
    } catch { return new Set(); }
  });
  const toggleExpand = useCallback((key) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      try { sessionStorage.setItem("pda-dispatch-expanded", JSON.stringify(Array.from(next))); } catch { /* ignore */ }
      return next;
    });
  }, []);

  usePdaScenarioHint("Dispatch");

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const statusParam = view === "completed" ? "delivered" : "packed";
      const res = await axios.get(`${API}/dispatch/queue`, { params: { status: statusParam } });
      const rows = view === "completed"
        ? (res.data || []).filter((r) => r.kind === "delivered")
        : (res.data || []).filter((r) => r.kind === "order" || r.kind === "missed_bag");
      setOrders(rows);
    } catch (e) {
      toast.error("Couldn't load dispatch queue");
    } finally {
      setLoading(false);
    }
  }, [view]);

  useEffect(() => { reload(); }, [reload]);

  const groups = useMemo(() => {
    const m = new Map();
    // Extract the delivered *day* (YYYY-MM-DD) from ISO `most_recent_delivered_at`
    // — used by the Completed view to sort by "day actually delivered", newest on top.
    const deliveredDay = (o) => (o.most_recent_delivered_at || "").slice(0, 10);
    for (const o of orders) {
      let key, meta;
      if (groupBy === "run") {
        key = o.run_id || "__unassigned__";
        meta = o.run_id
          ? { runId: o.run_id, runName: o.run_name, driver: o.run_driver, vehicle: o.run_vehicle, date: o.run_delivery_date, notes: o.run_notes }
          : { unassigned: true };
      } else {
        // Completed view groups by ACTUAL delivered day; Ready view by
        // scheduled delivery_date.
        key = (view === "completed" ? deliveredDay(o) : o.delivery_date) || "No date";
        meta = { date: key };
      }
      const bucket = m.get(key) || { key, meta, rows: [] };
      bucket.rows.push(o);
      m.set(key, bucket);
    }

    // Compute the newest delivered day within each bucket — that day drives
    // the bucket's position in Completed view ("最新送达的那一天排最上面").
    const buckets = Array.from(m.values());
    for (const b of buckets) {
      b.__newestDeliveredDay = b.rows.reduce((max, r) => {
        const d = deliveredDay(r);
        return d > max ? d : max;
      }, "");
    }
    const dateDir = view === "completed" ? -1 : 1;

    buckets.sort((a, b) => {
      // Unassigned bucket always last (in run mode).
      const aU = a.meta?.unassigned ? 1 : 0;
      const bU = b.meta?.unassigned ? 1 : 0;
      if (aU !== bU) return aU - bU;

      if (view === "completed") {
        // Rank by newest delivered day (DESC). Falls back to run/day metadata
        // and then run name for stable ordering on ties.
        const cmp = (b.__newestDeliveredDay || "").localeCompare(a.__newestDeliveredDay || "");
        if (cmp !== 0) return cmp;
        if (groupBy === "date") return (b.key || "").localeCompare(a.key || "");
        return (a.meta?.runName || "").localeCompare(b.meta?.runName || "");
      }

      // Ready view: original behaviour (by scheduled date asc, then run name).
      if (groupBy === "date") return dateDir * (a.key || "").localeCompare(b.key || "");
      const ad = dateDir * (a.meta?.date || "").localeCompare(b.meta?.date || "");
      if (ad !== 0) return ad;
      return (a.meta?.runName || "").localeCompare(b.meta?.runName || "");
    });

    // Within a bucket:
    //  - Completed view → alphabetical by hotel_name.
    //  - Ready view    → keep delivery_date + order_number sort as before.
    for (const b of buckets) {
      if (view === "completed") {
        b.rows.sort((a, c) =>
          (a.hotel_name || "").localeCompare(c.hotel_name || "")
          || (a.order_number || "").localeCompare(c.order_number || "")
        );
      } else {
        b.rows.sort((a, c) =>
          dateDir * ((a.delivery_date || "").localeCompare(c.delivery_date || ""))
          || (a.order_number || "").localeCompare(c.order_number || "")
        );
      }
    }
    return buckets;
  }, [orders, groupBy, view]);

  return (
    <PdaLayout
      title="Dispatch"
      subtitle={loading ? "Loading…" : `${orders.length} ${view === "completed" ? "delivered" : "order(s) ready"}`}
      onBack={() => navigate("/pda/menu")}
      onLogout={onLogout}
    >
      {/* Ready / Completed view toggle */}
      <div className="flex rounded-md border bg-white mb-2" data-testid="pda-dispatch-view">
        <button
          onClick={() => setView("ready")}
          data-testid="pda-dispatch-view-ready"
          className={`flex-1 py-2 text-xs font-bold rounded-l-md flex items-center justify-center gap-1 ${view === "ready" ? "bg-brand-navy text-white" : "text-neutral-600"}`}
        >
          <Truck className="h-3.5 w-3.5" />
          Ready
        </button>
        <button
          onClick={() => setView("completed")}
          data-testid="pda-dispatch-view-completed"
          className={`flex-1 py-2 text-xs font-bold rounded-r-md flex items-center justify-center gap-1 ${view === "completed" ? "bg-emerald-600 text-white" : "text-neutral-600"}`}
        >
          <PackageCheck className="h-3.5 w-3.5" />
          Completed
        </button>
      </div>

      {/* Group-by toggle removed — drivers always view by run on the PDA. */}

      {loading ? (
        <div className="py-10 flex flex-col items-center text-neutral-500">
          <Loader2 className="h-6 w-6 animate-spin" />
          <div className="text-sm mt-2">Loading orders…</div>
        </div>
      ) : orders.length === 0 ? (
        <div className="py-10 text-center text-neutral-500">
          {view === "completed"
            ? <PackageCheck className="h-10 w-10 mx-auto mb-2 opacity-40" />
            : <Truck className="h-10 w-10 mx-auto mb-2 opacity-40" />}
          <div className="text-sm">
            {view === "completed" ? "No delivered orders yet" : "No packed orders waiting"}
          </div>
          <button onClick={reload} data-testid="pda-dispatch-refresh" className="mt-3 text-amber-600 underline text-sm">Refresh</button>
        </div>
      ) : (
        <div className="space-y-4">
          {groups.map((g) => (
            <RunBlock
              key={g.key}
              bucket={g}
              groupBy={groupBy}
              view={view}
              navigate={navigate}
              isExpanded={expanded.has(g.key)}
              onToggle={() => toggleExpand(g.key)}
            />
          ))}
        </div>
      )}
    </PdaLayout>
  );
}

// Individual run/date block — bold header, then a list of stops.
// Collapsed by default; tap the header to expand. Sticky per session
// (see parent's `expanded` state / sessionStorage).
function RunBlock({ bucket, groupBy, view, navigate, isExpanded, onToggle }) {
  const { meta, rows } = bucket;
  const isRun = groupBy === "run" && !meta?.unassigned;
  const isCompletedView = view === "completed";
  const done = rows.filter((r) => r.status === "dispatched" || r.kind === "delivered").length;
  const packedBagCount = rows.reduce((s, o) => {
    const bp = o.bag_progress || {};
    return s + (bp.packed ?? (o.bags || []).filter((b) => b.status === "packed").length);
  }, 0);
  return (
    <div className="rounded-lg border-2 border-neutral-200 bg-white overflow-hidden">
      {/* Header — tap to expand/collapse */}
      <button
        type="button"
        onClick={onToggle}
        data-testid={`pda-dispatch-run-toggle-${bucket.key}`}
        aria-expanded={isExpanded}
        className={`w-full text-left px-3 py-2 active:brightness-95 ${isRun ? (isCompletedView ? "bg-emerald-700 text-white" : "bg-brand-navy text-white") : meta?.unassigned ? "bg-amber-50 border-b border-amber-200" : "bg-neutral-50 border-b"}`}
      >
        {isRun ? (
          <>
            <div className="flex items-center gap-2">
              <ChevronRight
                className={`h-4 w-4 shrink-0 transition-transform ${isExpanded ? "rotate-90" : ""}`}
              />
              <div className="flex items-center gap-1.5 min-w-0 flex-1">
                <RouteIcon className="h-3.5 w-3.5 shrink-0" />
                <span className="font-bold text-sm truncate">{meta.runName || "Run"}</span>
              </div>
              <span className="text-[10px] font-mono bg-white/20 rounded-full px-2 py-0.5 whitespace-nowrap">
                {isCompletedView ? `${rows.length} delivered` : `${done}/${rows.length} done`}
              </span>
            </div>
            <div className="flex items-center gap-3 text-[11px] text-white/80 mt-1 pl-6 flex-wrap">
              {meta.driver ? <span className="flex items-center gap-1"><User className="h-3 w-3" />{meta.driver}</span> : <span className="italic opacity-60">No driver</span>}
              {meta.vehicle ? <span className="flex items-center gap-1"><Car className="h-3 w-3" />{meta.vehicle}</span> : null}
              {meta.date ? <span className="flex items-center gap-1"><Calendar className="h-3 w-3" />{meta.date}</span> : null}
              {!isCompletedView && packedBagCount > 0 ? (
                <span className="flex items-center gap-1 ml-auto"><PackageCheck className="h-3 w-3" />{packedBagCount} bags</span>
              ) : null}
            </div>
            {meta.notes ? (
              <div className="mt-1.5 px-2 py-1 bg-amber-100 border-l-4 border-amber-600 text-amber-900 text-[11px] rounded-sm whitespace-pre-wrap" data-testid={`run-notes-${meta.runId}`}>
                <span className="font-bold uppercase tracking-wide text-[9px] block">Notes</span>
                {meta.notes}
              </div>
            ) : null}
          </>
        ) : (
          <div className="flex items-center gap-1.5 text-xs font-bold text-neutral-700">
            <ChevronRight
              className={`h-4 w-4 shrink-0 transition-transform ${isExpanded ? "rotate-90" : ""}`}
            />
            {meta?.unassigned
              ? <><Truck className="h-3.5 w-3.5" /><span>{isCompletedView ? "No run assigned" : "Unassigned"}</span></>
              : <><Calendar className="h-3.5 w-3.5" /><span>{meta.date}</span></>}
            <span className="text-neutral-400 font-normal ml-1">· {rows.length}</span>
          </div>
        )}
      </button>

      {/* Stops — only rendered when expanded to keep the initial scan short */}
      {isExpanded && (
        <div className="divide-y">
          {rows.map((o) => {
            const bp = o.bag_progress || {};
            const packedCount = bp.packed ?? (o.bags || []).filter((b) => b.status === "packed").length;
            const isDone = o.status === "dispatched" || o.kind === "delivered";
            return (
              <button
                key={`${o.order_id}-${o.kind}`}
                onClick={() => navigate(`/pda/dispatch/${o.order_id}`)}
                data-testid={`pda-dispatch-order-${o.order_id}`}
                className={`w-full text-left px-3 py-3 hover:bg-amber-50 active:bg-amber-100 ${isDone ? "bg-emerald-50/40" : ""}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-1.5 min-w-0 flex-1">
                    {isDone && <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-600" />}
                    <div className="font-display font-bold text-base truncate">{o.hotel_name || "—"}</div>
                    {o.is_pickup && (
                      <span
                        data-testid={`pda-dispatch-pickup-${o.order_id}`}
                        className="text-[9px] font-bold uppercase tracking-wider rounded px-1.5 py-0.5 bg-purple-100 text-purple-800 border border-purple-300 shrink-0"
                      >
                        Pickup
                      </span>
                    )}
                  </div>
                  <span className={`text-[11px] uppercase tracking-wider rounded-full px-2 py-0.5 whitespace-nowrap ${isDone ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`}>
                    {isDone ? "delivered" : `${packedCount} bag${packedCount === 1 ? "" : "s"}`}
                  </span>
                </div>
                <div className="text-[11px] text-neutral-500 font-mono mt-0.5">#{o.order_number}</div>
                {o.hotel_address ? (
                  <div className="text-xs text-neutral-600 mt-1 flex items-start gap-1">
                    <MapPin className="h-3 w-3 mt-0.5 shrink-0" />
                    <span className="line-clamp-2">{o.hotel_address}{o.suburb ? `, ${o.suburb}` : ""}</span>
                  </div>
                ) : null}
                {o.special_note ? (
                  <div
                    className="mt-1 px-2 py-1 bg-amber-100 border-l-4 border-amber-600 text-amber-900 text-[11px] rounded-sm whitespace-pre-wrap"
                    data-testid={`pda-hotel-note-${o.order_id}`}
                  >
                    <span className="font-bold">Note:</span> {o.special_note}
                  </div>
                ) : null}
                {groupBy === "run" && o.delivery_date ? (
                  <div className="text-[10px] uppercase tracking-wider text-neutral-400 mt-1">
                    {o.delivery_date}
                  </div>
                ) : null}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
